import React from 'react'
// libs
import styled from 'styled-components'
import { useQuery } from '@apollo/client/react'
// components
import { TreeWrapper } from 'components/Tree'
import ObjectivesTreeTheme from './ObjectivesTreeTheme'
import { LoaderWithOverlay } from 'components/UI/Loader'
import { reactVirtualizedClassName, Tree } from 'components/PageComponents/Plans/Edit/components/ObjectivesList'
// constants
import { Modals } from 'constants/modals'
import { ENTITIES } from 'constants/entities'
import { NODE_CONTENT_RENDERER_SIZES } from 'constants/index'
// context
import { DetailsModalActionsContext } from 'containers/MinimizedObjectivesList/layers/Business/DetailsModalActions'
import { MinimizedObjectivesListActionsContext } from 'containers/MinimizedObjectivesList/layers/Business/Actions'
import { MinimizedObjectivesListAPIContext } from 'containers/MinimizedObjectivesList/layers/API'
// hooks
import { useShowModal } from 'hooks'
// graphql
import { PLAN_DETAILS_TREE_QUERY } from 'graphql/plan/queries'
// themes
import { colors } from 'themes'
// utils
import { safeGetOr } from 'utils/fp'
import { isArchived } from 'containers/ObjectivesList/layers/API/utils'
import { first, flow, get } from 'lodash'
import { applyTreeDeepLevels } from 'utils/objective'
import { map as mapFp, orderBy, pipe } from 'lodash/fp'
import { getKey, getNodeKey, getParentKey } from 'utils/tree'
import { getFlatDataFromTree, getTreeFromFlatData } from 'react-sortable-tree'

const Wrapper = styled('div')`
  background: ${colors.white100};
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-top: 20px;

  ${TreeWrapper} {
    flex-grow: 1;
  }

  .objectives-list-virtualized-class {
    > div {
      width: calc(100% - 10px) !important;
    }
  }
`

const MinimizedObjectivesList = ({ currentPlan, parentPlan, isSubPlanCreation = false, isSubPlan = false }) => {
  const { handleAddToSubPlan, handleRemoveFromSubPlan } = React.useContext(MinimizedObjectivesListActionsContext)
  const { handleSetShowModalActions } = React.useContext(DetailsModalActionsContext)
  const { planItems } = React.useContext(MinimizedObjectivesListAPIContext)

  const targetPlan = isSubPlanCreation ? parentPlan : currentPlan
  const levels = flow((item) => get(item, 'planType.levels', []), orderBy('position', 'asc'))(targetPlan)
  const planTypeLevelIds = mapFp('id', levels)
  const rootLevel = first(levels)

  const { loading, data } = useQuery(PLAN_DETAILS_TREE_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        planId: targetPlan.id,
        referenced: false
      }
    }
  })

  // redux
  const { hideModal: hideActionModal, showModal: showActionModal } = useShowModal({
    modalType: Modals.ACTION_MINIMIZED_MODAL
  })

  const { hideModal: hideKPIModal, showModal: showKPIModal } = useShowModal({ modalType: Modals.KPI_MINIMIZED_MODAL })
  const { hideModal: hideObjectiveModal, showModal: showObjectiveModal } = useShowModal({
    modalType: Modals.OBJECTIVE_MINIMIZED_MODAL
  })

  if (!rootLevel) {
    return null
  }

  // methods
  const showActionMinimizedModal = (data) => {
    hideKPIModal()
    hideObjectiveModal()
    showActionModal({ ...data })
  }

  const showKPIMinimizedModal = (data) => {
    hideActionModal()
    hideObjectiveModal()
    showKPIModal({ ...data })
  }

  const showObjectiveMinimizedModal = (data) => {
    hideActionModal()
    hideKPIModal()
    showObjectiveModal({ ...data })
  }

  const generateNewNode = (treeItem) => {
    const additional = {}

    const newNode = {
      ...treeItem,
      rootLevel,
      title: treeItem.entity.description,
      position: treeItem.treePosition
    }

    const targetPlan = isSubPlanCreation ? currentPlan : parentPlan

    if (treeItem.entityType === ENTITIES.PLAN_OBJECTIVE) {
      additional.handleClick = handleObjectiveClick({ id: treeItem.entityId })
      additional.handleAddClick = handleAddToSubPlan(newNode, targetPlan, sourceItems)
      additional.handleRemoveClick = handleRemoveFromSubPlan(newNode)
    }

    if (treeItem.entityType === ENTITIES.PLAN_ACTION) {
      additional.handleClick = handleActionClick({ id: treeItem.entityId })
      additional.handleAddClick = handleAddToSubPlan(newNode, targetPlan, sourceItems)
      additional.handleRemoveClick = handleRemoveFromSubPlan(newNode)
    }

    if (treeItem.entityType === ENTITIES.PLAN_KPI) {
      additional.handleClick = handleKPIClick({ id: treeItem.entityId })
      additional.handleAddClick = handleAddToSubPlan(newNode, targetPlan, sourceItems)
      additional.handleRemoveClick = handleRemoveFromSubPlan(newNode)
    }

    return {
      ...additional,
      ...treeItem,
      currentPlan,
      isSubPlan,
      isSubPlanCreation,
      rootLevel,
      level: treeItem.level,
      linkingItems: {
        sourceLinkingItems: treeItem.entity.sourceLinkingItems || [],
        destinationLinkingItems: treeItem.entity.destinationLinkingItems || []
      },
      parentPlan: { ...parentPlan, planItems },
      position: treeItem.treePosition,
      title: treeItem.entity.description
    }
  }

  const handleObjectiveClick =
    ({ id }) =>
    async () => {
      await showObjectiveMinimizedModal({ id, isSubPlan, parentPlan, sourceItems, hideMenu: true })

      if (typeof handleSetShowModalActions === 'function') {
        handleSetShowModalActions(true)
      }
    }

  const handleActionClick =
    ({ id }) =>
    async () => {
      await showActionMinimizedModal({ id, isSubPlan, parentPlan, sourceItems, hideMenu: true })

      if (typeof handleSetShowModalActions === 'function') {
        handleSetShowModalActions(true)
      }
    }

  const handleKPIClick =
    ({ id }) =>
    async () => {
      await showKPIMinimizedModal({ id, isSubPlan, parentPlan, sourceItems, hideMenu: true })

      if (typeof handleSetShowModalActions === 'function') {
        handleSetShowModalActions(true)
      }
    }

  const prepareSource = (source) => {
    const orderedFlatTree = orderBy('position', 'asc')(source)

    const tree = getTreeFromFlatData({
      flatData: orderedFlatTree,
      getKey,
      getParentKey,
      rootKey: null
    })

    applyTreeDeepLevels(tree, { levels, planTypeLevelIds })

    const flatTreeWithLevels = pipe(
      (array) =>
        getFlatDataFromTree({
          treeData: array,
          getNodeKey,
          ignoreCollapsed: false
        }),
      mapFp('node')
    )(tree)

    const flatTree = mapFp(generateNewNode)(flatTreeWithLevels)

    return getTreeFromFlatData({
      flatData: flatTree,
      getKey,
      getParentKey,
      rootKey: null
    })
  }

  const fetchedPlanItems = safeGetOr([], 'planItems.data')(data)
  const activePlanItems = fetchedPlanItems.filter((item) => !isArchived(item))

  const entities = activePlanItems.flatMap((item) => {
    if (item.planObjective) {
      return {
        ...item,
        parentId: fetchedPlanItems.some((entity) => entity.id === item.parentId) ? item.parentId : null,
        entity: item.planObjective,
        entityId: item.planObjective.id,
        entityType: ENTITIES.PLAN_OBJECTIVE
      }
    } else if (item.planAction) {
      return {
        ...item,
        parentId: fetchedPlanItems.some((entity) => entity.id === item.parentId) ? item.parentId : null,
        entity: item.planAction,
        entityId: item.planAction.id,
        entityType: ENTITIES.PLAN_ACTION
      }
    } else if (item.planKPI) {
      return {
        ...item,
        parentId: fetchedPlanItems.some((entity) => entity.id === item.parentId) ? item.parentId : null,
        entity: item.planKPI,
        entityId: item.planKPI.id,
        entityType: ENTITIES.PLAN_KPI
      }
    }
  })

  const sourceItems = entities.filter(Boolean).map(({ entityId, treePosition }) => ({ entityId, treePosition }))

  const sourceTree = prepareSource(entities)

  return (
    <>
      <LoaderWithOverlay isLoading={loading} />
      {!loading && (
        <>
          <Wrapper>
            <Tree
              canDrag={false}
              sourceTree={sourceTree}
              theme={ObjectivesTreeTheme}
              rowHeight={NODE_CONTENT_RENDERER_SIZES.ROW_HEIGHT}
              isLoading={loading}
              reactVirtualizedListProps={{ className: reactVirtualizedClassName, style: { outline: 'none' } }}
            />
          </Wrapper>
        </>
      )}
    </>
  )
}

export default MinimizedObjectivesList
